import React, { useState } from "react";
import { Row, Col, Typography, Input, Button, notification } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { usePreference } from "../context/PreferenceContext";
import {
    IconBrandFacebook,
    IconBrandInstagram,
    IconBrandTwitter,
    IconBrandLinkedin,
    IconSend,
    IconChevronUp
} from "@tabler/icons-react";
import "../css/Footer.css";
import PublicServ from "../service/PublicServ";

const { Title, Text } = Typography;

const Footer = () => {
    const { t } = usePreference();
    const navigate = useNavigate();
    const location = useLocation();
    const currentYear = new Date().getFullYear();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const scrollToTop = () => {
        // Scroll window
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Scroll document element (handles html height: 100%)
        if (document.documentElement) {
            document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Scroll body (handles body height: 100%)
        if (document.body) {
            document.body.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Scroll root container if it's the scrollable area
        const root = document.getElementById('root');
        if (root) {
            root.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleNavigate = (path, targetSection) => {
        if (targetSection) {
            if (location.pathname === path) {
                // Already on the target page, scroll manually
                const el = document.getElementById(targetSection);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            } else {
                // Navigate and let the target page handle scrolling via location.state
                navigate(path, { state: { scrollTo: targetSection } });
            }
        } else {
            navigate(path);
            scrollToTop();
        }
    };

    const handleSubscribe = async () => {
        if (!email) {
            notification.error({
                message: t('error') || 'Error',
                description: t('please_enter_email') || 'Please enter your email address'
            });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            notification.error({
                message: t('error') || 'Error',
                description: t('invalid_email') || 'Please enter a valid email address'
            });
            return;
        }

        setLoading(true);
        try {
            const res = await PublicServ.subscribeNewsletter({ email });
            notification.success({
                message: t('success') || 'Success',
                description: res.message || t('subscribed_successfully') || 'Subscribed successfully!'
            });
            setEmail("");
        } catch (err) {
            notification.error({
                message: t('error') || 'Error',
                description: err.message || t('subscription_failed') || 'Subscription failed.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="main-footer">
            <div className="footer-container">
                <Row gutter={[48, 40]}>
                    {/* Brand Section */}
                    <Col xs={24} lg={8}>
                        <div className="footer-brand">
                            <Title level={3} className="footer-logo">
                                NEARZON
                            </Title>
                            <Paragraph className="footer-about">
                                Empowering shoppers with premium tools and a curated marketplace to find the best products with ease.
                            </Paragraph>

                            <div className="footer-socials">
                                <a href="#"><IconBrandFacebook size={22} stroke={1.5} /></a>
                                <a href="#"><IconBrandInstagram size={22} stroke={1.5} /></a>
                                <a href="#"><IconBrandTwitter size={22} stroke={1.5} /></a>
                                <a href="#"><IconBrandLinkedin size={22} stroke={1.5} /></a>
                            </div>

                            <button className="back-to-top-btn" onClick={scrollToTop}>
                                <IconChevronUp size={18} />
                                BACK TO TOP
                            </button>
                        </div>
                    </Col>

                    {/* Links Group */}
                    <Col xs={12} md={8} lg={4}>
                        <Title level={5} className="footer-heading">Site Map</Title>
                        <ul className="footer-links">
                            <li><a onClick={() => handleNavigate("/")}>{t('home')}</a></li>
                            <li><a onClick={() => handleNavigate("/all-products")}>{t('all_products')}</a></li>
                            <li><a onClick={() => handleNavigate("/", "shops-section")}>{t('shops')}</a></li>
                            <li><a onClick={() => handleNavigate("/contact")}>{t('contact_us')}</a></li>
                            <li><a onClick={() => handleNavigate("/start-selling")}>{t('start_selling')}</a></li>
                        </ul>
                    </Col>

                    <Col xs={12} md={8} lg={4}>
                        <Title level={5} className="footer-heading">Legal</Title>
                        <ul className="footer-links">
                            <li><a onClick={() => handleNavigate("/privacy-policy")}>Privacy Policy</a></li>
                            <li><a onClick={() => handleNavigate("/terms-and-conditions")}>Terms of Services</a></li>
                            <li><a onClick={() => handleNavigate("/shipping-policy")}>Shipping Policy</a></li>
                            <li><a onClick={() => handleNavigate("/contact")}>{t('help_center')}</a></li>
                        </ul>
                    </Col>

                    {/* Newsletter Section */}
                    <Col xs={24} md={8} lg={8}>
                        <Title level={5} className="footer-heading">{t('newsletter')}</Title>
                        <Paragraph className="footer-text">
                            Subscribe to receive the latest offers and architectural updates from Nearzon.
                        </Paragraph>
                        <div className="footer-newsletter">
                            <Input
                                placeholder="Your Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onPressEnter={handleSubscribe}
                            />
                            <button
                                onClick={handleSubscribe}
                                disabled={loading}
                            >
                                <IconSend size={18} />
                            </button>
                        </div>
                    </Col>
                </Row>

                <div style={{ height: '50px' }}></div>
            </div>

            {/* Gold Bottom Bar */}
            <div className="footer-bottom-bar">
                <div className="footer-bottom-content">
                    <Text>© {currentYear} Nearzon. All Rights Reserved.</Text>

                    <div className="footer-legal-bar">
                        <a onClick={() => handleNavigate("/privacy-policy")}>Privacy Policy</a>
                        <a onClick={() => handleNavigate("/terms-and-conditions")}>Terms & Conditions</a>
                        <a onClick={() => handleNavigate("/shipping-policy")}>Shipping Policy</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

const Paragraph = ({ children, className }) => <p className={className}>{children}</p>;

export default Footer;

